import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, ShoppingBag, AlertCircle, Calendar, Share2, UserCheck, UserX } from 'lucide-react';
import { multiNivelService, type IndicacoesDashboardData } from '@/services/multi-nivel.service';

type PeriodOption = '7' | '30' | '90' | 'custom';

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getDateRange(period: PeriodOption, customStart?: string, customEnd?: string): { startDate: string; endDate: string } {
  if (period === 'custom' && customStart && customEnd) {
    return { startDate: customStart, endDate: customEnd };
  }
  const days = parseInt(period);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

export const IndicacoesDashboard: React.FC = () => {
  const [data, setData] = useState<IndicacoesDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange(selectedPeriod, customStartDate, customEndDate);

      const response = await multiNivelService.getIndicacoesDashboard(startDate, endDate);
      setData(response);
      setLoading(false);
    } catch (err) {
      console.error('Full error:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido ao carregar dados');
      setLoading(false);
    }
  }, [selectedPeriod, customStartDate, customEndDate]);

  useEffect(() => {
    if (selectedPeriod === 'custom' && (!customStartDate || !customEndDate)) return;
    fetchData();
  }, [fetchData, selectedPeriod, customStartDate, customEndDate]);

  const periodLabel = selectedPeriod === 'custom'
    ? `${customStartDate} a ${customEndDate}`
    : `Últimos ${selectedPeriod} dias`;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center min-h-40">
            <div className="text-center">
              <div className="animate-spin mb-4">
                <Share2 className="w-8 h-8 text-primary mx-auto" />
              </div>
              <p className="text-muted-foreground">Carregando dados de indicações...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-destructive">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Erro ao carregar dados</p>
              <p className="text-sm text-destructive/80">{error}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalIndicados = (data?.total_clientes_indicados || 0) + (data?.total_revendedoras_indicadas || 0);
  const totalAtivos = (data?.clientes_ativos || 0) + (data?.revendedoras_ativas || 0);
  const totalFaltamAtivar = (data?.clientes_faltam_ativar || 0) + (data?.revendedoras_faltam_ativar || 0);

  return (
    <div className="space-y-4">
      {/* Seletor de Período */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Período:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: '7' as PeriodOption, label: '7 dias' },
                { value: '30' as PeriodOption, label: '30 dias' },
                { value: '90' as PeriodOption, label: '90 dias' },
                { value: 'custom' as PeriodOption, label: 'Personalizado' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedPeriod(opt.value)}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    selectedPeriod === opt.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {selectedPeriod === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  max={customEndDate || formatDate(new Date())}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-border bg-background"
                />
                <span className="text-sm text-muted-foreground">até</span>
                <input
                  type="date"
                  value={customEndDate}
                  max={formatDate(new Date())}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="px-2 py-1.5 text-sm rounded-md border border-border bg-background"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Row 1: Totais Gerais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Total de Indicações */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <Share2 className="w-4 h-4 text-primary" />
              </div>
              <span>Total de Indicações</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Novas indicações no período</p>
              <p className="text-3xl font-bold text-foreground">
                {totalIndicados.toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">Período</p>
              <p className="text-sm font-semibold text-foreground">{periodLabel}</p>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Total Ativos */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-primary" />
              </div>
              <span>Total Ativos</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Indicados que atingiram o mínimo</p>
              <p className="text-3xl font-bold text-foreground">
                {totalAtivos.toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">Taxa de ativação</p>
              <p className="text-lg font-semibold text-foreground">
                {totalIndicados > 0 ? ((totalAtivos / totalIndicados) * 100).toFixed(1) : '0'}%
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Total Faltam Ativar */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <UserX className="w-4 h-4 text-primary" />
              </div>
              <span>Faltam Ativar</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Indicados pendentes de ativação</p>
              <p className="text-3xl font-bold text-foreground">
                {totalFaltamAtivar.toLocaleString('pt-BR')}
              </p>
            </div>
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground">Pendentes</p>
              <p className="text-lg font-semibold text-foreground">
                {totalIndicados > 0 ? ((totalFaltamAtivar / totalIndicados) * 100).toFixed(1) : '0'}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Detalhamento Clientes e Revendedoras */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Card 4: Clientes Indicados */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <ShoppingBag className="w-4 h-4 text-primary" />
              </div>
              <span>Clientes Indicados</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total de novos clientes</p>
              <p className="text-3xl font-bold text-foreground">
                {data?.total_clientes_indicados || 0}
              </p>
            </div>
            <div className="pt-3 border-t border-border space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-muted-foreground">Ativos (compras ≥ R$100)</span>
                </div>
                <span className="text-sm font-semibold text-foreground">{data?.clientes_ativos || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40"></div>
                  <span className="text-sm text-muted-foreground">Faltam ativar</span>
                </div>
                <span className="text-sm font-semibold text-muted-foreground">{data?.clientes_faltam_ativar || 0}</span>
              </div>
              {(data?.total_clientes_indicados || 0) > 0 && (
                <div className="w-full bg-muted rounded-full h-2 mt-1">
                  <div
                    className="bg-emerald-500 h-2 rounded-full transition-all"
                    style={{ width: `${((data?.clientes_ativos || 0) / (data?.total_clientes_indicados || 1)) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card 5: Revendedoras Indicadas */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                <Users className="w-4 h-4 text-primary" />
              </div>
              <span>Revendedoras Indicadas</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total de novas revendedoras</p>
              <p className="text-3xl font-bold text-foreground">
                {data?.total_revendedoras_indicadas || 0}
              </p>
            </div>
            <div className="pt-3 border-t border-border space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm text-muted-foreground">Ativas (vendas ≥ R$500)</span>
                </div>
                <span className="text-sm font-semibold text-foreground">{data?.revendedoras_ativas || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/40"></div>
                  <span className="text-sm text-muted-foreground">Faltam ativar</span>
                </div>
                <span className="text-sm font-semibold text-muted-foreground">{data?.revendedoras_faltam_ativar || 0}</span>
              </div>
              {(data?.total_revendedoras_indicadas || 0) > 0 && (
                <div className="w-full bg-muted rounded-full h-2 mt-1">
                  <div
                    className="bg-emerald-500 h-2 rounded-full transition-all"
                    style={{ width: `${((data?.revendedoras_ativas || 0) / (data?.total_revendedoras_indicadas || 1)) * 100}%` }}
                  ></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IndicacoesDashboard;
